import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether we've confirmed backend reachability (not just browser online) */
  isBackendReachable: boolean;
  /** Timestamp of last successful backend ping */
  lastOnlineAt: number | null;
  /** How long (ms) the user has been offline, or 0 if online */
  offlineDurationMs: number;
}

const PING_INTERVAL_MS = 30_000; // check backend every 30s while offline
const PING_TIMEOUT_MS = 5_000;

/**
 * Monitors network connectivity using both browser events and backend health checks.
 *
 * - `isOnline`: navigator.onLine (immediate, but can be wrong on captive portals)
 * - `isBackendReachable`: confirmed via HEAD request to the API
 *
 * When the browser goes offline, we stop API queries.
 * When it comes back online, we verify the backend is reachable before resuming.
 */
export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isBackendReachable, setIsBackendReachable] = useState(true); // assume reachable on mount
  const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(() =>
    navigator.onLine ? Date.now() : null
  );
  const [offlineSince, setOfflineSince] = useState<number | null>(() =>
    navigator.onLine ? null : Date.now()
  );
  const [now, setNow] = useState(Date.now());

  const pingBackend = useCallback(async (): Promise<boolean> => {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

      const res = await fetch(`${baseUrl}/health`, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeout);
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  // Browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Verify backend is actually reachable
      pingBackend().then((reachable) => {
        setIsBackendReachable(reachable);
        if (reachable) {
          setLastOnlineAt(Date.now());
          setOfflineSince(null);
        }
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsBackendReachable(false);
      setOfflineSince((prev) => prev ?? Date.now());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pingBackend]);

  // Periodic ping while offline to detect recovery
  useEffect(() => {
    if (isOnline && isBackendReachable) return;

    const interval = setInterval(async () => {
      const reachable = await pingBackend();
      if (reachable) {
        setIsOnline(true);
        setIsBackendReachable(true);
        setLastOnlineAt(Date.now());
        setOfflineSince(null);
      }
    }, PING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isOnline, isBackendReachable, pingBackend]);

  // Tick timer for duration display while offline
  useEffect(() => {
    if (!offlineSince) return;

    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [offlineSince]);

  return {
    isOnline: isOnline && isBackendReachable,
    isBackendReachable,
    lastOnlineAt,
    offlineDurationMs: offlineSince ? now - offlineSince : 0,
  };
}
